import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { parseInviteBody } from "@/lib/meeting-invite";
import { parseRuleThresholds, type FollowUpRuleThresholds } from "@/lib/follow-up-rules";

/**
 * CRM-konfig fra system_config. queryKey ["system-config", key] dekkes av
 * registry-entryen for system_config.
 */

async function readConfigValue(key: string): Promise<unknown> {
  const { data, error } = await supabase.from("system_config").select("value").eq("key", key).maybeSingle();
  if (error) throw error;
  return data?.value ?? null;
}

/** Gjeldende terskler for oppfølgingsreglene, med standard for manglende nøkler. */
export function useFollowUpRuleThresholds() {
  return useQuery({
    queryKey: ["system-config", "crm_follow_up_rules"],
    queryFn: async (): Promise<FollowUpRuleThresholds> => parseRuleThresholds(await readConfigValue("crm_follow_up_rules")),
    staleTime: 5 * 60 * 1000,
  });
}

export interface SalesMeetingConfig {
  /** 0 = søndag ... 6 = lørdag (Date.getDay). */
  weekday: number;
  hour: number;
}

export const DEFAULT_SALES_MEETING: SalesMeetingConfig = { weekday: 1, hour: 9 };

export function parseSalesMeeting(value: unknown): SalesMeetingConfig {
  const v = value && typeof value === "object" ? (value as Record<string, unknown>) : {};
  const weekday = typeof v.weekday === "number" && v.weekday >= 0 && v.weekday <= 6 ? v.weekday : DEFAULT_SALES_MEETING.weekday;
  const hour = typeof v.hour === "number" && v.hour >= 0 && v.hour <= 23 ? v.hour : DEFAULT_SALES_MEETING.hour;
  return { weekday, hour };
}

/** Når salgsmøtet er (default mandag 09:00), til «N igjen før salgsmøtet». */
export function useSalesMeetingConfig() {
  return useQuery({
    queryKey: ["system-config", "crm_sales_meeting"],
    queryFn: async (): Promise<SalesMeetingConfig> => parseSalesMeeting(await readConfigValue("crm_sales_meeting")),
    staleTime: 5 * 60 * 1000,
  });
}

/** Forhåndsutfylt melding til kunden i bookingdialogen (crm_meeting_email_templates.invite_body). */
export function useInviteBodyTemplate() {
  return useQuery({
    queryKey: ["system-config", "crm_meeting_email_templates"],
    queryFn: async (): Promise<string> => parseInviteBody(await readConfigValue("crm_meeting_email_templates")),
    staleTime: 5 * 60 * 1000,
  });
}
